/**
 * Upgrade cards.
 *
 * Three offered at the end of every floor, one taken, kept for the run. The
 * pool is written against weapon *tags* rather than weapon ids, so a card can
 * be a bet on what you will find later — "wartime weapons hit harder" is a
 * different decision from "shotguns hit harder", and both are different from
 * "everything reloads faster". With four hundred guns in the library that bet
 * is a real one.
 *
 * Two things about how this is built.
 *
 * Effects are data, not callbacks. A card used to be a function that reached
 * into the modifiers and did whatever it liked, which is unanswerable: you
 * cannot tell what a pool of forty of those does without running all of them,
 * and you cannot simulate a build at all. Now a card is a list of typed
 * effects and one interpreter applies them, so the pool can be read, tested
 * and balanced as a table.
 *
 * And a card should change a verb, not a number. Fifteen per cent more damage
 * is not a decision, it is arithmetic; a magazine whose first half hits like a
 * truck and whose second half is nearly useless changes when you reload, which
 * is a different way of playing. The plain multipliers are still here because
 * a run needs somewhere to put a floor you had nothing else for — but the ones
 * that matter are at the bottom of the file.
 */

import type { Weapon, WeaponAction, WeaponClass, WeaponEra, WeaponOrigin } from './weapons.ts';
import type { Trait } from './traits.ts';

export interface Modifiers {
  maxHealth: number;
  moveSpeed: number;
  /** Flat multipliers applied to every weapon. */
  damage: number;
  fireRate: number;
  reloadSpeed: number;
  magazine: number;
  spread: number;
  /** Multiplier on the recoil climb. Lower is a flatter, more controllable gun. */
  recoil: number;
  pierce: number;
  headshot: number;
  lifesteal: number;
  /** Chance a kill drops a health pickup. */
  scavenge: number;
  /** Damage taken is multiplied by this. */
  armour: number;

  // --- the ones that change how a gun is used ------------------------------

  /**
   * Damage multiplier on the first half of a magazine. The second half takes
   * the reciprocal penalty, so the card is a reason to reload early rather
   * than free damage.
   */
  glassMagazine: number;
  /** Rate of fire gained per kill, decaying a few seconds after the last one. */
  momentum: number;
  /** Spread multiplier once you have been still for a moment. */
  deadeye: number;
  /** Damage passed to the nearest other enemy when one dies. */
  chain: number;
  /** Rounds returned to the magazine by a hit to the head. */
  refundOnHead: number;
  /** Fraction of damage carried by a shot that bounces off a wall. */
  ricochet: number;

  /** Per-tag multipliers. */
  byClass: Partial<Record<WeaponClass, number>>;
  byAction: Partial<Record<WeaponAction, number>>;
  byEra: Partial<Record<WeaponEra, number>>;
  byOrigin: Partial<Record<WeaponOrigin, number>>;
  /** Actions that no longer cost their cycle time. */
  instantCycle: Partial<Record<WeaponAction, boolean>>;
  /** Enemies hit are slowed to this fraction for a moment. */
  slowOnHit: number;
  /** Explosions on kill. */
  detonate: number;
  /**
   * Rules the run has been granted, on top of whatever the gun already does.
   *
   * A trait is not a number and cannot be one — "every third round explodes"
   * has no magnitude — so it lives beside the multipliers rather than in them.
   * Most are gated on a weapon tag as well, which is what makes them a bet
   * rather than a bonus: taking the one about wartime weapons is a decision
   * about what you hope to find on the next three floors.
   */
  traits: Partial<Record<Trait, boolean>>;
}

/** The fields an effect may move. Everything else is structure. */
type Stat = {
  [K in keyof Modifiers]: Modifiers[K] extends number ? K : never;
}[keyof Modifiers];

export function baseModifiers(): Modifiers {
  return {
    maxHealth: 100,
    moveSpeed: 1,
    damage: 1,
    fireRate: 1,
    reloadSpeed: 1,
    magazine: 1,
    spread: 1,
    recoil: 1,
    pierce: 0,
    headshot: 1,
    lifesteal: 0,
    scavenge: 0,
    armour: 1,
    glassMagazine: 1,
    momentum: 0,
    deadeye: 1,
    chain: 0,
    refundOnHead: 0,
    ricochet: 0,
    byClass: {},
    byAction: {},
    byEra: {},
    byOrigin: {},
    instantCycle: {},
    slowOnHit: 1,
    detonate: 0,
    traits: {},
  };
}

// -----------------------------------------------------------------------------
// Effects, as data

export type Effect =
  /** Multiply a number. */
  | { stat: Stat; mul: number }
  /** Add to a number. */
  | { stat: Stat; add: number }
  /** Take the lower of the current value and this one. */
  | { stat: Stat; atMost: number }
  /** Multiply the damage of weapons carrying one tag. */
  | { tag: 'class' | 'action' | 'era' | 'origin'; is: string; mul: number }
  /** Remove the hand-cycling cost from an action. */
  | { cycles: WeaponAction }
  /** Grant a rule. */
  | { trait: Trait };

const GROUPS = {
  class: 'byClass', action: 'byAction', era: 'byEra', origin: 'byOrigin',
} as const;

/**
 * One interpreter for the whole pool.
 *
 * Order matters and is the order the cards were taken in, which is the only
 * order a player can reason about.
 */
export function applyEffects(m: Modifiers, effects: readonly Effect[]): void {
  for (const e of effects) {
    if ('trait' in e) {
      m.traits[e.trait] = true;
    } else if ('cycles' in e) {
      m.instantCycle[e.cycles] = true;
    } else if ('tag' in e) {
      const bag = m[GROUPS[e.tag]] as Record<string, number | undefined>;
      bag[e.is] = (bag[e.is] ?? 1) * e.mul;
    } else if ('mul' in e) {
      (m[e.stat] as number) *= e.mul;
    } else if ('add' in e) {
      (m[e.stat] as number) += e.add;
    } else {
      (m[e.stat] as number) = Math.min(m[e.stat] as number, e.atMost);
    }
  }
}

// -----------------------------------------------------------------------------

/**
 * What a card is about.
 *
 * Used for two things: making sure three cards offered together are not three
 * versions of the same idea, and noticing when one of them would build on
 * something already taken.
 */
export type CardTag =
  | 'health' | 'speed' | 'damage' | 'rate' | 'reload' | 'magazine'
  | 'accuracy' | 'recoil' | 'armour' | 'crit' | 'pierce' | 'sustain'
  | 'explosive' | 'class' | 'action' | 'era' | 'origin' | 'transform';

export interface Card {
  id: string;
  name: string;
  text: string;
  /** Higher is rarer. */
  tier: number;
  /** Cards that only make sense once you have seen a bit of the run. */
  from?: number;
  /** How many times it may be taken. */
  max?: number;
  /** What it is about. */
  tags: CardTag[];
  /** Cards this cannot be held alongside. */
  conflicts?: string[];
  effects: Effect[];
}

export const CARDS: Card[] = [
  // --- plain, always useful -------------------------------------------------
  { id: 'vitality', name: 'Field Rations', text: '+30 maximum health, and heal for it now.', tier: 0, tags: ['health'], effects: [{ stat: 'maxHealth', add: 30 }] },
  { id: 'boots', name: 'Better Boots', text: '+12% movement speed.', tier: 0, tags: ['speed'], effects: [{ stat: 'moveSpeed', mul: 1.12 }] },
  { id: 'powder', name: 'Hotter Powder', text: '+15% damage with everything.', tier: 0, tags: ['damage'], effects: [{ stat: 'damage', mul: 1.15 }] },
  { id: 'springs', name: 'Lighter Springs', text: '+12% rate of fire.', tier: 0, tags: ['rate'], effects: [{ stat: 'fireRate', mul: 1.12 }] },
  { id: 'drills', name: 'Reload Drills', text: 'Reload 25% faster.', tier: 0, tags: ['reload'], effects: [{ stat: 'reloadSpeed', mul: 1.25 }] },
  { id: 'pouches', name: 'Deep Pouches', text: '+40% magazine size.', tier: 0, tags: ['magazine'], effects: [{ stat: 'magazine', mul: 1.4 }] },
  { id: 'choke', name: 'Choke', text: '25% tighter spread.', tier: 0, tags: ['accuracy'], effects: [{ stat: 'spread', mul: 0.75 }] },
  { id: 'compensator', name: 'Compensator', text: 'Recoil climbs 30% less.', tier: 0, max: 2, tags: ['recoil'], effects: [{ stat: 'recoil', mul: 0.7 }] },
  { id: 'muzzle_brake', name: 'Muzzle Brake', text: 'Recoil climbs 50% less, but spread widens a little.', tier: 1, tags: ['recoil', 'accuracy'], effects: [{ stat: 'recoil', mul: 0.5 }, { stat: 'spread', mul: 1.15 }] },
  { id: 'plates', name: 'Plate Carrier', text: 'Take 15% less damage.', tier: 1, tags: ['armour'], effects: [{ stat: 'armour', mul: 0.85 }] },
  { id: 'scope', name: 'Etched Reticle', text: '+35% headshot damage.', tier: 1, tags: ['crit'], effects: [{ stat: 'headshot', mul: 1.35 }] },

  // --- keyed off class ------------------------------------------------------
  { id: 'buckshot', name: 'Cut Shells', text: 'Shotguns deal +40% damage.', tier: 1, tags: ['class'], effects: [{ tag: 'class', is: 'shotgun', mul: 1.4 }] },
  { id: 'stubby', name: 'Stubby Grips', text: 'Submachine guns deal +35% damage.', tier: 1, tags: ['class'], effects: [{ tag: 'class', is: 'smg', mul: 1.35 }] },
  { id: 'longshot', name: 'Long Shot', text: 'Marksman rifles deal +45% damage.', tier: 1, tags: ['class'], effects: [{ tag: 'class', is: 'dmr', mul: 1.45 }] },
  { id: 'suppressing', name: 'Suppressing Fire', text: 'Machine guns deal +40% damage.', tier: 1, tags: ['class'], effects: [{ tag: 'class', is: 'lmg', mul: 1.4 }] },
  { id: 'sidearm_pride', name: 'Duellist', text: 'Pistols deal +70% damage.', tier: 1, tags: ['class'], effects: [{ tag: 'class', is: 'pistol', mul: 1.7 }] },
  { id: 'wheelgun', name: 'Six for Six', text: 'Revolvers deal +80% damage.', tier: 1, tags: ['class'], effects: [{ tag: 'class', is: 'revolver', mul: 1.8 }] },
  { id: 'carbine_drill', name: 'Carbine Drill', text: 'Carbines and rifles deal +30% damage.', tier: 1, tags: ['class'], effects: [{ tag: 'class', is: 'rifle', mul: 1.3 }, { tag: 'class', is: 'carbine', mul: 1.3 }] },
  { id: 'battle_order', name: 'Battle Order', text: 'Battle rifles deal +45% damage.', tier: 1, tags: ['class'], effects: [{ tag: 'class', is: 'battle', mul: 1.45 }] },
  { id: 'glassing', name: 'Range Card', text: 'Sniper rifles deal +50% damage.', tier: 2, tags: ['class'], effects: [{ tag: 'class', is: 'sniper', mul: 1.5 }] },
  { id: 'materiel', name: 'Materiel Permit', text: 'Anti-materiel rifles deal +40% damage and pierce one more body.', tier: 2, from: 6, tags: ['class', 'pierce'], effects: [{ tag: 'class', is: 'antimat', mul: 1.4 }, { stat: 'pierce', add: 1 }] },
  { id: 'ordnance', name: 'Ordnance Licence', text: 'Launchers deal +50% damage.', tier: 2, tags: ['class', 'explosive'], effects: [{ tag: 'class', is: 'launcher', mul: 1.5 }] },

  // --- keyed off action -----------------------------------------------------
  { id: 'autoloader', name: 'Autoloader', text: 'Pump actions no longer need pumping.', tier: 2, tags: ['action', 'rate'], effects: [{ cycles: 'pump' }] },
  { id: 'straight_pull', name: 'Straight Pull', text: 'Bolt actions cycle instantly.', tier: 2, tags: ['action', 'rate'], effects: [{ cycles: 'bolt' }] },
  { id: 'trigger_job', name: 'Trigger Job', text: 'Semi-automatic weapons deal +40% damage.', tier: 1, tags: ['action'], effects: [{ tag: 'action', is: 'semi', mul: 1.4 }] },
  { id: 'full_auto', name: 'Full Auto Sear', text: 'Automatic weapons deal +25% damage.', tier: 1, tags: ['action'], effects: [{ tag: 'action', is: 'auto', mul: 1.25 }] },
  { id: 'burst_timing', name: 'Burst Timing', text: 'Burst weapons deal +45% damage.', tier: 1, tags: ['action'], effects: [{ tag: 'action', is: 'burst', mul: 1.45 }] },
  { id: 'lever_love', name: 'Lever Work', text: 'Lever actions deal double damage.', tier: 2, tags: ['action'], effects: [{ tag: 'action', is: 'lever', mul: 2.0 }] },
  { id: 'capacitor', name: 'Overcharged Cell', text: 'Charge weapons deal +60% damage.', tier: 2, tags: ['action'], effects: [{ tag: 'action', is: 'charge', mul: 1.6 }] },
  { id: 'breech_drill', name: 'Breech Drill', text: 'Break-action weapons deal +70% damage.', tier: 2, tags: ['action'], effects: [{ tag: 'action', is: 'breech', mul: 1.7 }] },

  // --- keyed off era --------------------------------------------------------
  { id: 'wartime', name: 'War Surplus', text: 'Wartime weapons deal +35% damage.', tier: 1, tags: ['era'], effects: [{ tag: 'era', is: 'wartime', mul: 1.35 }] },
  { id: 'cold_stores', name: 'Cold Stores', text: 'Cold-war weapons deal +35% damage.', tier: 1, tags: ['era'], effects: [{ tag: 'era', is: 'cold', mul: 1.35 }] },
  { id: 'modern_kit', name: 'Modern Kit', text: 'Modern weapons deal +30% damage.', tier: 1, tags: ['era'], effects: [{ tag: 'era', is: 'modern', mul: 1.3 }] },
  { id: 'antiquarian', name: 'Antiquarian', text: 'Antique weapons deal triple damage.', tier: 2, tags: ['era'], effects: [{ tag: 'era', is: 'antique', mul: 3.0 }] },
  { id: 'black_project', name: 'Black Project', text: 'Prototype weapons deal +50% damage.', tier: 2, from: 5, tags: ['era'], effects: [{ tag: 'era', is: 'prototype', mul: 1.5 }] },

  // --- keyed off origin -----------------------------------------------------
  { id: 'continental_pact', name: 'Continental Pact', text: 'French, Belgian and Czech designs deal +45% damage.', tier: 1, tags: ['origin'], effects: [{ tag: 'origin', is: 'continental', mul: 1.45 }] },
  { id: 'german_pact', name: 'German Engineering', text: 'German, Austrian and Swiss designs deal +45% damage.', tier: 1, tags: ['origin'], effects: [{ tag: 'origin', is: 'german', mul: 1.45 }] },
  { id: 'eastern_pact', name: 'Eastern Steel', text: 'Eastern-bloc designs deal +45% damage.', tier: 1, tags: ['origin'], effects: [{ tag: 'origin', is: 'eastern', mul: 1.45 }] },
  { id: 'mediterranean_pact', name: 'Mediterranean Licence', text: 'Italian, Spanish and Israeli designs deal +45% damage.', tier: 1, tags: ['origin'], effects: [{ tag: 'origin', is: 'mediterranean', mul: 1.45 }] },
  { id: 'anglo_pact', name: 'Anglo Warrant', text: 'American and British designs deal +45% damage.', tier: 1, tags: ['origin'], effects: [{ tag: 'origin', is: 'anglo', mul: 1.45 }] },
  { id: 'unmarked', name: 'No Fixed Abode', text: 'Weapons of no clear lineage deal double damage.', tier: 2, from: 4, tags: ['origin'], effects: [{ tag: 'origin', is: 'unmarked', mul: 2.0 }] },

  // --- behavioural ----------------------------------------------------------
  { id: 'pierce', name: 'Hardened Cores', text: 'Shots punch through one more body.', tier: 1, max: 3, tags: ['pierce'], effects: [{ stat: 'pierce', add: 1 }] },
  { id: 'lifesteal', name: 'Field Transfusion', text: 'Heal 4 health per kill.', tier: 1, tags: ['sustain'], effects: [{ stat: 'lifesteal', add: 4 }] },
  { id: 'scavenge', name: 'Scavenger', text: '18% of kills drop a medkit.', tier: 1, max: 3, tags: ['sustain'], effects: [{ stat: 'scavenge', add: 0.18 }] },
  { id: 'sticky', name: 'Tar Rounds', text: 'Anything you hit is slowed for a moment.', tier: 2, tags: ['transform'], effects: [{ stat: 'slowOnHit', atMost: 0.55 }] },
  { id: 'detonate', name: 'Sympathetic Detonation', text: 'Kills explode, hurting anything nearby.', tier: 2, max: 3, tags: ['explosive'], effects: [{ stat: 'detonate', add: 26 }] },
  { id: 'glass', name: 'Glass Cannon', text: '+55% damage, but your maximum health is halved.', tier: 2, from: 3, tags: ['damage', 'health'], conflicts: ['juggernaut'], effects: [{ stat: 'damage', mul: 1.55 }, { stat: 'maxHealth', mul: 0.5 }] },
  { id: 'juggernaut', name: 'Juggernaut', text: '+60 health, but you move 10% slower.', tier: 1, tags: ['health', 'speed'], conflicts: ['glass'], effects: [{ stat: 'maxHealth', add: 60 }, { stat: 'moveSpeed', mul: 0.9 }] },

  // --- the ones that change how you play ------------------------------------
  //
  // A percentage is arithmetic. These change a verb: when you reload, whether
  // you stand still, what a kill is worth, where a shot goes when it misses.
  {
    id: 'glass_magazine', name: 'Glass Magazine',
    text: 'The first half of every magazine deals double damage. The second half deals 40% less.',
    tier: 2, from: 3, tags: ['transform', 'magazine'],
    effects: [{ stat: 'glassMagazine', mul: 2.0 }],
  },
  {
    id: 'momentum', name: 'Momentum',
    text: 'Each kill adds 12% rate of fire for four seconds. Taking damage clears it.',
    tier: 2, from: 3, max: 2, tags: ['transform', 'rate'],
    effects: [{ stat: 'momentum', add: 0.12 }],
  },
  {
    id: 'deadeye', name: 'Deadeye',
    text: 'Standing still for half a second removes almost all of your spread.',
    tier: 2, from: 2, tags: ['transform', 'accuracy'],
    effects: [{ stat: 'deadeye', atMost: 0.12 }],
  },
  {
    id: 'chain', name: 'Arc Discharge',
    text: 'A kill passes 40% of its damage to the nearest thing beside it.',
    tier: 2, from: 4, max: 2, tags: ['transform', 'explosive'],
    effects: [{ stat: 'chain', add: 0.4 }],
  },
  {
    id: 'headhunter', name: 'Headhunter',
    text: 'A hit to the head puts two rounds back in the magazine.',
    tier: 2, from: 4, tags: ['transform', 'crit'],
    effects: [{ stat: 'refundOnHead', add: 2 }],
  },
  {
    id: 'ricochet', name: 'Ricochet Protocol',
    text: 'A shot that hits a wall bounces to whatever it can see. Direct hits deal 15% less.',
    tier: 3, from: 5, tags: ['transform'],
    effects: [{ stat: 'ricochet', add: 0.75 }, { stat: 'damage', mul: 0.85 }],
  },

  // --- the ones that break a rule -------------------------------------------
  //
  // Every card above changes a quantity. These change what the gun *is*, and
  // they are gated on a tag, so taking one is a bet on the library rather than
  // an upgrade to what is in your hands. That is the whole reason to have four
  // hundred guns: a card that only matters if you find a pump action is a card
  // that makes the next weapon on the floor interesting.
  //
  // They are also, deliberately, slightly ridiculous. A run somebody describes
  // to someone else afterwards is one where something happened that should not
  // have — and nobody has ever described a run by its damage multiplier.
  {
    id: 'slamfire', name: 'Slamfire',
    text: 'Pump actions fire as fast as you can hold the trigger. The cone opens badly.',
    tier: 3, from: 4, tags: ['action', 'transform', 'rate'],
    effects: [{ trait: 'slamfire' }],
  },
  {
    id: 'third_shell', name: 'Every Third Shell',
    text: 'Every third round in the magazine is loaded with something it should not be.',
    tier: 3, from: 4, tags: ['transform', 'explosive'],
    effects: [{ trait: 'third_shell' }],
  },
  {
    id: 'mag_grenade', name: 'Primed Magazines',
    text: 'The magazine you throw away goes off where it lands.',
    tier: 3, from: 5, tags: ['transform', 'explosive', 'reload'],
    effects: [{ trait: 'mag_grenade' }],
  },
  {
    id: 'battle_rhythm', name: 'Battle Rhythm',
    text: 'Wartime weapons gain 9% damage per kill, up to eight. A shot that hits nothing clears it.',
    tier: 3, from: 4, tags: ['era', 'transform', 'damage'],
    effects: [{ trait: 'battle_rhythm' }],
  },
  {
    id: 'deep_freeze', name: 'Cold Stores Requisition',
    text: 'Cold-war weapons cool what they hit. Keep firing and it can barely move.',
    tier: 3, from: 5, tags: ['era', 'transform'],
    effects: [{ trait: 'deep_freeze' }],
  },
];

/** Damage multiplier for a specific weapon under the run's modifiers. */
export function weaponMultiplier(m: Modifiers, w: Weapon): number {
  return (
    m.damage *
    (m.byClass[w.cls] ?? 1) *
    (m.byAction[w.action] ?? 1) *
    (m.byEra[w.era] ?? 1) *
    (m.byOrigin[w.origin] ?? 1)
  );
}

/**
 * Three cards to choose from.
 *
 * Weighted to depth, so late floors are richer, and shaped by two rules that
 * matter more than the weighting:
 *
 * Never three of the same idea. Three damage multipliers is not a choice, it
 * is a formality, and the whole point of the screen is the decision.
 *
 * Lean toward what you are already doing. A run wants to become something, and
 * a card that builds on one you took two floors ago is the one worth being
 * offered — but only leaning, because a build you cannot be surprised out of
 * is a build you are stuck in.
 */
export function offer(depth: number, taken: string[], roll: () => number): Card[] {
  const counts = new Map<string, number>();
  for (const t of taken) counts.set(t, (counts.get(t) ?? 0) + 1);
  const held = new Set(taken);
  const heldTags = new Set<CardTag>();
  for (const id of held) {
    const c = CARDS.find((x) => x.id === id);
    for (const t of c?.tags ?? []) heldTags.add(t);
  }

  const pool = CARDS.filter((c) => {
    if (c.from && depth < c.from) return false;
    if ((counts.get(c.id) ?? 0) >= (c.max ?? 1)) return false;
    if (c.conflicts?.some((other) => held.has(other))) return false;
    return true;
  });

  const out: Card[] = [];
  const bag = [...pool];
  const shown = new Set<CardTag>();
  while (out.length < 3 && bag.length) {
    const weights = bag.map((c) => {
      let w = c.tier === 0 ? 6 : c.tier === 1 ? 3 + depth * 0.2 : c.tier === 2 ? 1 + depth * 0.35 : 0.4 + depth * 0.3;
      // Something you have been building toward is worth offering.
      if (c.tags.some((t) => heldTags.has(t))) w *= 1.6;
      // Something already on this screen is not.
      if (c.tags.some((t) => shown.has(t))) w *= 0.12;
      return w;
    });
    const total = weights.reduce((a, b) => a + b, 0);
    let r = roll() * total;
    let idx = 0;
    for (; idx < bag.length; idx++) {
      r -= weights[idx];
      if (r <= 0) break;
    }
    const [picked] = bag.splice(Math.min(idx, bag.length - 1), 1);
    for (const t of picked.tags) shown.add(t);
    out.push(picked);
  }
  return out;
}
